import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import DesignTokens from '../../styles/DesignTokens';

const ChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${DesignTokens.spacing.lg};
`;

const SVGWrapper = styled.div`
  position: relative;
  width: 220px;
  height: 220px;
`;

const CenterText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
`;

const TotalLabel = styled.div`
  font-size: ${DesignTokens.typography.fontSize.xs};
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 4px;
`;

const TotalAmount = styled.div`
  font-size: ${DesignTokens.typography.fontSize['2xl']};
  font-weight: ${DesignTokens.typography.fontWeight.bold};
  color: white;
`;

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${DesignTokens.spacing.sm};
  justify-content: center;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: ${DesignTokens.borderRadius.full};
`;

const LegendDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.$color};
`;

const LegendLabel = styled.span`
  font-size: ${DesignTokens.typography.fontSize.xs};
  color: rgba(255, 255, 255, 0.7);
`;

const LegendValue = styled.span`
  font-size: ${DesignTokens.typography.fontSize.xs};
  font-weight: ${DesignTokens.typography.fontWeight.semibold};
  color: white;
  margin-left: 4px;
`;

const SEGMENT_COLORS = ['#4285F4', '#34A853', '#FBBC04', '#8B5CF6'];

const safeNum = (v) => (typeof v === 'number' && !Number.isNaN(v) ? v : 0);

const formatAmount = (amount, currency) => {
  if (typeof amount !== 'number' || Number.isNaN(amount)) return '';
  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `$${amount.toLocaleString()}`;
  }
};

const BudgetDonutChart = ({ segments, total, currency }) => {
  const radius = 85;
  const strokeWidth = 28;
  const center = 110;
  const circumference = 2 * Math.PI * radius;
  const safeTotal = safeNum(total);

  // Calculate segment offsets
  let cumulativePercent = 0;
  const segmentData = segments.map((seg, i) => {
    const percent = safeTotal > 0 ? (safeNum(seg.amount) / safeTotal) * 100 : 0;
    const offset = cumulativePercent;
    cumulativePercent += percent;
    return {
      ...seg,
      percent,
      offset,
      color: seg.color || SEGMENT_COLORS[i % SEGMENT_COLORS.length],
    };
  });

  const formattedTotal = typeof total === 'number'
    ? formatAmount(total, currency)
    : total || formatAmount(0, currency);

  return (
    <ChartContainer>
      <SVGWrapper>
        <svg width="220" height="220" viewBox="0 0 220 220">
          {/* Background circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="rgba(255,255,255,0.08)"
            strokeWidth={strokeWidth}
          />
          {/* Segments */}
          {segmentData.map((seg, i) => {
            const dashLength = (seg.percent / 100) * circumference;
            const dashOffset = -(seg.offset / 100) * circumference;
            return (
              <motion.circle
                key={seg.label}
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={seg.color}
                strokeWidth={strokeWidth}
                strokeDasharray={`${dashLength} ${circumference - dashLength}`}
                strokeDashoffset={dashOffset}
                strokeLinecap="butt"
                transform={`rotate(-90 ${center} ${center})`}
                initial={{ strokeDasharray: `0 ${circumference}` }}
                animate={{ strokeDasharray: `${dashLength} ${circumference - dashLength}` }}
                transition={{ duration: 0.8, delay: i * 0.15, ease: 'easeOut' }}
              />
            );
          })}
        </svg>
        <CenterText>
          <TotalLabel>Total</TotalLabel>
          <TotalAmount>{formattedTotal}</TotalAmount>
        </CenterText>
      </SVGWrapper>

      <LegendContainer>
        {segmentData.map(seg => (
          <LegendItem key={seg.label}>
            <LegendDot $color={seg.color} />
            <LegendLabel>{seg.label}</LegendLabel>
            <LegendValue>
              {typeof seg.amount === 'number' ? formatAmount(seg.amount, currency) : seg.amount}
            </LegendValue>
          </LegendItem>
        ))}
      </LegendContainer>
    </ChartContainer>
  );
};

export default BudgetDonutChart;
